import APIClient, { CPEAPIClient, endpoints } from 'shared/api';
import { EstimatedAmounts } from 'components/claims/estimated';
import { AdjustAmounts } from 'components/claims/adjust';
import { ExpenseFields } from 'components/claims/expense';
import { Depreciation } from 'components/claims/depreciation';
import { DepreciationCalculation } from 'components/claims/depreciationCalculation';

export const getOrCreateDamageAnalysisCPE = async (
  claimId: string,
  initialClaimReserve?: number,
): Promise<string> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis'.replace(
    '{claimId}',
    claimId,
  );
  const responseGet = await CPEAPIClient.get(endpoint);

  if (responseGet.data.length > 0) {
    console.warn('Damage Analysis Id Get: ', responseGet.data[0].id);

    return responseGet.data[0].id;
  }

  const responsePost = await CPEAPIClient.post(endpoint, {
    initialClaimReserve: initialClaimReserve ?? 0,
  });

  console.warn('Damage Analysis Id Post: ', responsePost.data.id);

  return responsePost.data[0].id;
};

//List of data to be sent to CPE, not everything is being used, wil delete this later
//     partsCost: '',
//     porcDesc: '0',
//     discountAmount: '',
//     subtotalParts: '',
//     partsCostWithoutDiscount: '',
//     partsCostTotal: '',
//     expenseAmount: '',
//     materialsCost: '',
//     laborCost: '',
//     ivuLaborAmount: '',
//     ivuPartsAmount: '',
//     estimatedTotalAmount: '',
//     porcIVA: '0',
//     ivaAmount: '0',
//   });

export const postEstimatesCPE = async (
  claimId: string,
  damageAnalysisId: string,
  body: EstimatedAmounts,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/estimates'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);

  await CPEAPIClient.post(endpoint, {
    expenseAmount: body.expenseAmount,
    date: new Date(),
    costOfNewParts: body.partsCost,
    newPartsDiscountPercentage: body.porcDesc,
    costOfFullPriceParts: body.partsCostWithoutDiscount,
    costOfMaterials: body.materialsCost,
    costOfLabor: body.laborCost,
    taxOnLabor: body.ivuLaborAmount,
    taxOnParts: body.ivuPartsAmount,
    totalCostEstimate: body.estimatedTotalAmount,
    previousDamageCost: body.previousDamageCost,
  });
};

export const postAdjusmentsCPE = async (
  claimId: string,
  damageAnalysisId: string,
  body: AdjustAmounts,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/adjustments'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);

  const curUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const agentEmail = curUser?.userName || null;

  await CPEAPIClient.post(endpoint, {
    date: new Date(),
    agentEmail: agentEmail,
    totalCostEstimate: body.estimatedTotalAmount,
    totalToPay: body.totalToPay,
    depreciationPercentage: body.porcDepreciation,
    depreciationAmount: body.depreciationAmount,
    negligencePercentage: body.percNegligence,
    customerResponsabilityAmount: body.customerResponsabilityAmount,
    deductibleAmount: body.deductibleAmount,
    fines: body.fines,
    others: body.others,
    constructive: body.constructive,
    othersPlus: body.othersPlus,
    negligence: body.negligence,
    towCost: body.trailer,
    missingPartsCost: body.missingParts,
    advancedPayment: body.advancePayment,
    totalInsuranceCoverageAmount: body.totalInsuranceAmount,
    closedWithoutPayment: body.closedWithoutPayment,
    isTotalLoss: body.isTotalLoss || false,
  });
};

export const postAdditionalExpensesCPE = async (
  claimId: string,
  damageAnalysisId: string,
  body: ExpenseFields,
  documentUrl: string,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/additionalExpenses'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);

  await CPEAPIClient.post(endpoint, {
    date: new Date(),
    expenseType: body.expenseType,
    description: body.expenseDescription,
    amount: body.expenseAmount,
    documentUrl: documentUrl,
  });
};

export const postSubrogationCPE = async (
  claimId: string,
  damageAnalysisId: string,
  insuranceCompany: string,
  croquis: string | null,
  isSubrogate: boolean,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/subrogations'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);
  await CPEAPIClient.post(endpoint, {
    insuranceCompany: insuranceCompany,
    croquis: croquis,
    isSubrogation: isSubrogate,
  });
};

export const updateClaimSubrogationCPE = async (
  claimId: string,
  isSubrogate: boolean,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}'.replace('{claimId}', claimId);
  await CPEAPIClient.patch(endpoint, {
    isSubrogation: isSubrogate,
  });
};

export const getTotalLossCPE = async (
  claimId: string,
  damageAnalysisId: string,
): Promise<string> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);
  const response = await CPEAPIClient.get(endpoint);

  if (response.data.length === 0) {
    return '';
  }

  return response.data[0].id;
};

export const postTotalLossCPE = async (
  claimId: string,
  damageAnalysisId: string,
  recoveryAmount: number,
  updatedReserveAmount: number,
  totalLossDate: string,
): Promise<string> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);
  const response = await CPEAPIClient.post(endpoint, {
    adjustedClaimReserveAmount: updatedReserveAmount,
    salvageValue: recoveryAmount,
    date: totalLossDate,
  });

  return response.data.id;
};

export const updateTotalLossCPE = async (
  claimId: string,
  damageAnalysisId: string,
  totalLossId: string,
  recoveryAmount: number,
  updatedReserveAmount: number,
  totalLossDate: string,
): Promise<string> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses/{id}'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId)
    .replace('{id}', totalLossId);
  const response = await CPEAPIClient.patch(endpoint, {
    adjustedClaimReserveAmount: updatedReserveAmount,
    salvageValue: recoveryAmount,
    date: totalLossDate,
  });

  return response.data.id;
};

export const patchTotalLossCPE = async (
  claimId: string,
  damageAnalysisId: string,
  updatedReserveAmount: number,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);
  await CPEAPIClient.patch(endpoint, {
    adjustedClaimReserveAmount: updatedReserveAmount,
  });
};

export const updateClaimIsTotaLossCPE = async (
  claimId: string,
  isTotalLoss: boolean,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}'.replace('{claimId}', claimId);
  await CPEAPIClient.patch(endpoint, {
    isTotalLoss: isTotalLoss,
  });
};

export const postTotalLossComparablesCPE = async (
  claimId: string,
  damageAnalysisId: string,
  cpeTotalLossId: string,
  body: Depreciation,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses/{totalLossId}/comparables'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId)
    .replace('{totalLossId}', cpeTotalLossId);
  console.warn('Posting comparable');
  await CPEAPIClient.post(endpoint, {
    date: body.dateDepreciation,
    value: body.price,
    observation: body.observations,
    documentUrl: body.fileName,
  });
};

export const postTotalLossDepreciationCPE = async (
  claimId: string,
  damageAnalysisId: string,
  cpeTotalLossId: string,
  body: DepreciationCalculation,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses/{totalLossId}/depreciations'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId)
    .replace('{totalLossId}', cpeTotalLossId);

  await CPEAPIClient.post(endpoint, {
    startDate: body.PurchaseDate,
    lossDate: body.LossDate,
    newVehicleCost: body.TotalInsured,
    annualDepreciationRate: body.Percentage,
    paymentTotal: body.TotalDepreciation,
  });
};

export const updatedTotalLossDepreciationCPE = async (
  claimId: string,
  damageAnalysisId: string,
  cpeTotalLossId: string,
  id: string,
  body: DepreciationCalculation,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses/{totalLossId}/depreciations/{id}'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId)
    .replace('{totalLossId}', cpeTotalLossId)
    .replace('{id}', id);

  await CPEAPIClient.patch(endpoint, {
    startDate: body.PurchaseDate,
    lossDate: body.LossDate,
    newVehicleCost: body.TotalInsured,
    annualDepreciationRate: body.Percentage,
    paymentTotal: body.TotalDepreciation,
  });
};

export const getTotalLossDepreciationCPE = async (
  claimId: string,
  damageAnalysisId: string,
  cpeTotalLossId: string,
): Promise<string | undefined> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/totalLosses/{totalLossId}/depreciations'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId)
    .replace('{totalLossId}', cpeTotalLossId);

  const response = await CPEAPIClient.get(endpoint);
  if (response.data.length === 0) {
    return undefined;
  }

  return response.data[0].id;
};

export type ClaimRequestCPE = {
  description: string;
  email: string;
  notify: boolean;
};

export const postClaimRequestsCPE = async (
  claimId: string,
  damageAnalysisId: string,
  body: ClaimRequestCPE,
): Promise<void> => {
  const endpoint = '/v1/claims/{claimId}/damageAnalysis/{damageAnalysisId}/claimRequests'
    .replace('{claimId}', claimId)
    .replace('{damageAnalysisId}', damageAnalysisId);

  await CPEAPIClient.post(endpoint, {
    description: body.description,
    email: body.email,
    notify: body.notify,
  });
};
